<template>
  <div class="about-page">
    <!-- 页眉部分 -->
    <Header></Header>

    <!-- 导航菜单 -->
    <Nav></Nav>

    <!-- 英雄区域 -->
    <div class="hero">
      <div class="hero-content">
        <h1>{{ $t("research.heroTitle") }}</h1>
        <p>{{ $t("research.heroSubtitle") }}</p>
      </div>
    </div>

    <div class="research-container">
      <!-- 介绍部分 -->
      <div class="research-intro">
        <h2>{{ $t("research.ourAchievements") }}</h2>
        <p>
          {{ $t("research.achievementsIntro") }}
        </p>
      </div>

      <!-- Tabs -->
      <div class="research-tabs">
        <div
          class="research-tab"
          :class="{ active: activeTab === 'publications' }"
          @click="activeTab = 'publications'"
        >
          {{ $t("research.publications") }}
        </div>
        <div
          class="research-tab"
          :class="{ active: activeTab === 'projects' }"
          @click="activeTab = 'projects'"
        >
          {{ $t("research.projects") }}
        </div>
        <div
          class="research-tab"
          :class="{ active: activeTab === 'patents' }"
          @click="activeTab = 'patents'"
        >
          {{ $t("research.patents") }}
        </div>
        <div
          class="research-tab"
          :class="{ active: activeTab === 'softwareCopyrights' }"
          @click="activeTab = 'softwareCopyrights'"
        >
          {{ $t("research.softwareCopyrights") }}
        </div>
        <div
          class="research-tab"
          :class="{ active: activeTab === 'awards' }"
          @click="activeTab = 'awards'"
        >
          {{ $t("research.awards") }}
        </div>
        <div
          class="research-tab"
          :class="{ active: activeTab === 'videos' }"
          @click="activeTab = 'videos'"
        >
          {{ $t("research.videos") }}
        </div>
      </div>

      <!-- 内容区域 -->
      <div class="research-content">
        <!-- 学术论文（含 Highlight 二级分类） -->
        <div v-if="activeTab === 'publications'" class="publication-list">
          <div class="project-sub-tabs">
            <div
              class="project-sub-tab"
              :class="{ active: publicationSubTab === 'highlight' }"
              @click="publicationSubTab = 'highlight'"
            >
              {{ $t("research.highlight") }}
            </div>
            <div
              class="project-sub-tab"
              :class="{ active: publicationSubTab === 'publication' }"
              @click="publicationSubTab = 'publication'"
            >
              {{ $t("research.publications") }}
            </div>
          </div>

          <div v-if="publicationSubTab === 'highlight'">
            <div
              v-for="yearGroup in highlightPublicationsByYear"
              :key="yearGroup.year"
              class="year-group"
            >
              <div class="year-header">{{ yearGroup.year }}</div>
              <div
                class="publication-item"
                v-for="(paper, index) in yearGroup.items"
                :key="index"
              >
                <div class="item-image">
                  <img
                    v-if="paper.cover"
                    :src="paper.cover"
                    :alt="$t('research.paperCover')"
                  />
                  <span v-else>{{ $t("research.paperCover") }}</span>
                </div>
                <div class="item-content">
                  <div
                    class="publication-title"
                    @click="showPDF(paper.file, paper.title)"
                  >
                    {{ paper.title }}
                  </div>
                  <div class="publication-authors">{{ paper.authors }}</div>
                  <div class="publication-journal">{{ paper.journal }}</div>
                  <div class="publication-doi">{{ paper.doi }}</div>
                  <div class="publication-abstract">{{ paper.abstract }}</div>
                </div>
              </div>
            </div>
            <p v-if="loadingHighlightPublications" class="loading-tip">
              正在加载更多...
            </p>
            <p
              v-else-if="
                highlightPublicationsLoaded &&
                highlightPublications.length === 0
              "
              class="empty-tip"
            >
              暂无数据
            </p>
            <p
              v-else-if="
                highlightPublications.length > 0 &&
                highlightPublications.length >= highlightPublicationsTotal
              "
              class="no-more"
            >
              {{ $t("research.allHighlightLoaded") }}
            </p>
          </div>

          <div v-if="publicationSubTab === 'publication'">
            <div
              v-for="yearGroup in publicationsByYear"
              :key="yearGroup.year"
              class="year-group"
            >
              <div class="year-header">{{ yearGroup.year }}</div>
              <div
                class="publication-item"
                v-for="(paper, index) in yearGroup.items"
                :key="index"
              >
                <div class="item-image">
                  <img
                    v-if="paper.cover"
                    :src="paper.cover"
                    :alt="$t('research.paperCover')"
                  />
                  <span v-else>{{ $t("research.paperCover") }}</span>
                </div>
                <div class="item-content">
                  <div
                    class="publication-title"
                    @click="showPDF(paper.file, paper.title)"
                  >
                    {{ paper.title }}
                  </div>
                  <div class="publication-authors">{{ paper.authors }}</div>
                  <div class="publication-journal">{{ paper.journal }}</div>
                  <div class="publication-doi">{{ paper.doi }}</div>
                  <div class="publication-abstract">{{ paper.abstract }}</div>
                </div>
              </div>
            </div>
            <p v-if="loadingPublications" class="loading-tip">
              正在加载更多...
            </p>
            <p
              v-else-if="publicationsLoaded && publications.length === 0"
              class="empty-tip"
            >
              暂无数据
            </p>
            <p
              v-else-if="
                publications.length > 0 && publications.length >= publicationsTotal
              "
              class="no-more"
            >
              {{ $t("research.allPapersLoaded") }}
            </p>
          </div>
        </div>

        <!-- 科研项目（含纵向/横向二级分类） -->
        <div v-if="activeTab === 'projects'" class="project-list">
          <!-- 二级子分类 tab -->
          <div class="project-sub-tabs">
            <div
              class="project-sub-tab"
              :class="{ active: projectSubTab === 'vertical' }"
              @click="projectSubTab = 'vertical'"
            >
              {{ $t("research.verticalProjects") }}
            </div>
            <div
              class="project-sub-tab"
              :class="{ active: projectSubTab === 'horizontal' }"
              @click="projectSubTab = 'horizontal'"
            >
              {{ $t("research.horizontalProjects") }}
            </div>
          </div>

          <!-- 纵向项目列表 -->
          <div v-if="projectSubTab === 'vertical'">
            <div
              class="project-item project-item-text-only"
              v-for="(project, index) in verticalProjects"
              :key="index"
            >
              <div class="item-content">
                <div
                  class="project-title"
                  @click="showPDF(project.file, project.title)"
                >
                  {{ project.title }}
                </div>
                <div class="project-team">{{ project.team }}</div>
                <div class="project-source">{{ project.source }}</div>
                <div class="project-period">{{ project.period }}</div>
                <div class="project-desc">{{ project.desc }}</div>
              </div>
            </div>
            <p v-if="loadingVerticalProjects" class="loading-tip">
              正在加载更多...
            </p>
            <p
              v-else-if="verticalProjectsLoaded && verticalProjects.length === 0"
              class="empty-tip"
            >
              暂无数据
            </p>
            <p v-else-if="verticalProjects.length > 0 && verticalProjects.length >= verticalProjectsTotal" class="no-more">
              {{ $t("research.allProjectsLoaded") }}
            </p>
          </div>

          <!-- 横向项目列表 -->
          <div v-if="projectSubTab === 'horizontal'">
            <div
              class="project-item project-item-text-only"
              v-for="(project, index) in horizontalProjects"
              :key="index"
            >
              <div class="item-content">
                <div
                  class="project-title"
                  @click="showPDF(project.file, project.title)"
                >
                  {{ project.title }}
                </div>
                <div class="project-team">{{ project.team }}</div>
                <div class="project-source">{{ project.source }}</div>
                <div class="project-period">{{ project.period }}</div>
                <div class="project-desc">{{ project.desc }}</div>
              </div>
            </div>
            <p v-if="loadingHorizontalProjects" class="loading-tip">
              正在加载更多...
            </p>
            <p
              v-else-if="
                horizontalProjectsLoaded && horizontalProjects.length === 0
              "
              class="empty-tip"
            >
              暂无数据
            </p>
            <p v-else-if="horizontalProjects.length > 0 && horizontalProjects.length >= horizontalProjectsTotal" class="no-more">
              {{ $t("research.allProjectsLoaded") }}
            </p>
          </div>
        </div>

        <!-- 专利成果（按年份分组） -->
        <div v-if="activeTab === 'patents'" class="patent-list">
          <div
            v-for="yearGroup in patentsByYear"
            :key="yearGroup.year"
            class="year-group"
          >
            <div class="year-header">{{ yearGroup.year }}</div>
            <div
              class="patent-item patent-item-text-only"
              v-for="(patent, index) in yearGroup.items"
              :key="index"
            >
              <div class="item-content">
                <div
                  class="patent-title"
                  @click="showPDF(patent.file, patent.title)"
                >
                  {{ patent.title }}
                </div>
                <div class="patent-number">{{ patent.number }}</div>
                <div class="patent-inventors">{{ patent.inventors }}</div>
                <div class="patent-date">{{ patent.date }}</div>
                <div class="patent-abstract">{{ patent.abstract }}</div>
              </div>
            </div>
          </div>
          <p v-if="loadingPatents" class="loading-tip">
            正在加载更多...
          </p>
          <p
            v-else-if="patentsLoaded && patents.length === 0"
            class="empty-tip"
          >
            暂无数据
          </p>
          <p v-else-if="patents.length > 0 && patents.length >= patentsTotal" class="no-more">
            {{ $t("research.allPatentsLoaded") }}
          </p>
        </div>

        <!-- 软著成果（按年份分组） -->
        <div v-if="activeTab === 'softwareCopyrights'" class="patent-list">
          <div
            v-for="yearGroup in softwareCopyrightsByYear"
            :key="yearGroup.year"
            class="year-group"
          >
            <div class="year-header">{{ yearGroup.year }}</div>
            <div
              class="patent-item patent-item-text-only"
              v-for="(item, index) in yearGroup.items"
              :key="index"
            >
              <div class="item-content">
                <div
                  class="patent-title"
                  @click="showPDF(item.file, item.title)"
                >
                  {{ item.title }}
                </div>
                <div class="patent-number">{{ item.number }}</div>
                <div class="patent-inventors">{{ item.inventors }}</div>
                <div class="patent-date">{{ item.date }}</div>
                <div class="patent-abstract">{{ item.abstract }}</div>
              </div>
            </div>
          </div>
          <p v-if="loadingSoftwareCopyrights" class="loading-tip">
            正在加载更多...
          </p>
          <p
            v-else-if="
              softwareCopyrightsLoaded && softwareCopyrights.length === 0
            "
            class="empty-tip"
          >
            暂无数据
          </p>
          <p
            v-else-if="
              softwareCopyrights.length > 0 &&
              softwareCopyrights.length >= softwareCopyrightsTotal
            "
            class="no-more"
          >
            {{ $t("research.allSoftwareCopyrightsLoaded") }}
          </p>
        </div>

        <!-- 科研获奖（按年份分组） -->
        <div v-if="activeTab === 'awards'" class="award-list">
          <div
            v-for="yearGroup in awardsByYear"
            :key="yearGroup.year"
            class="year-group"
          >
            <div class="year-header">{{ yearGroup.year }}</div>
            <div
              class="award-item"
              v-for="(award, index) in yearGroup.items"
              :key="index"
            >
              <div class="item-image">
                <img
                  v-if="award.certificate"
                  :src="award.certificate"
                  :alt="$t('research.awardCert')"
                />
                <span v-else>{{ $t("research.awardCert") }}</span>
              </div>
              <div class="item-content">
                <div
                  class="award-title"
                  @click="showPDF(award.file, award.title)"
                >
                  {{ award.title }}
                </div>
                <div class="award-year">{{ award.year }}</div>
                <div class="award-recipients">{{ award.recipients }}</div>
                <div class="award-org">{{ award.org }}</div>
                <div class="award-desc">{{ award.desc }}</div>
              </div>
            </div>
          </div>
          <p v-if="loadingAwards" class="loading-tip">
            正在加载更多...
          </p>
          <p v-else-if="awardsLoaded && awards.length === 0" class="empty-tip">
            暂无数据
          </p>
          <p v-else-if="awards.length > 0 && awards.length >= awardsTotal" class="no-more">
            {{ $t("research.allAwardsLoaded") }}
          </p>
        </div>

        <!-- 成果视频 -->
        <div v-if="activeTab === 'videos'" class="video-list">
          <div class="video-item" v-for="(video, index) in videos" :key="index">
            <div class="item-image">
              <img
                v-if="video.cover"
                :src="video.cover"
                :alt="$t('research.videoCover')"
              />
              <span v-else>{{ $t("research.videoCover") }}</span>
            </div>
            <div class="item-content">
              <div
                class="project-title"
                @click="showVideo(video.file, video.title)"
              >
                {{ video.title }}
              </div>
              <div class="project-period">{{ video.year }}</div>
              <div class="project-desc">{{ video.desc }}</div>
            </div>
          </div>
          <p v-if="loadingVideos" class="loading-tip">
            正在加载更多...
          </p>
          <p v-else-if="videosLoaded && videos.length === 0" class="empty-tip">
            暂无数据
          </p>
          <p v-else-if="videos.length > 0 && videos.length >= videosTotal" class="no-more">
            {{ $t("research.allVideosLoaded") }}
          </p>
        </div>
      </div>
      <!-- 弹窗（PDF Modal） -->
      <div v-if="pdfVisible" class="pdf-modal">
        <div class="pdf-modal-content">
          <div class="pdf-modal-header">
            <h3>{{ pdfTitle }}</h3>
            <span class="close-btn" @click="pdfVisible = false">×</span>
          </div>
          <div class="pdf-modal-body">
            <iframe
              :src="pdfFile"
              width="100%"
              height="600px"
              frameborder="0"
            ></iframe>
          </div>
        </div>
      </div>

      <!-- 弹窗（Video Modal） -->
      <div v-if="videoVisible" class="pdf-modal">
        <div class="pdf-modal-content">
          <div class="pdf-modal-header">
            <h3>{{ videoTitle }}</h3>
            <span class="close-btn" @click="closeVideo">×</span>
          </div>
          <div class="pdf-modal-body">
            <video
              ref="videoPlayer"
              :key="videoFile"
              width="100%"
              height="600"
              controls
              controlslist="nodownload"
              crossorigin="anonymous"
            >
              <source :src="videoFile" type="video/mp4" />
              {{ $t("research.browserNotSupport") }}
            </video>
          </div>
        </div>
      </div>
    </div>
    <!-- 页脚 -->
    <Footer></Footer>
  </div>
</template>

<script>
import Header from "../../components/head.vue";
import Nav from "../../components/nav.vue";
import Footer from "../../components/footer.vue";
import {
  PublicationList,
  ProjectList,
  PatentList,
  PwardList,
  AchievementList,
  VideoList,
} from "../../api/research";

export default {
  name: "ResearchPage",
  components: { Header, Nav, Footer },
  data() {
    return {
      activeTab: "publications",
      publicationSubTab: "highlight",
      projectSubTab: "vertical", // 科研项目二级分类：vertical/horizontal
      pdfVisible: false, // 控制 PDF 弹窗显示
      pdfFile: "", // 当前 PDF 地址
      pdfTitle: "", // 当前标题
      videoVisible: false, // 控制视频弹窗显示
      videoFile: "", // 当前视频地址
      videoTitle: "", // 当前视频标题
      publications: [],
      publicationsByYear: [],
      highlightPublications: [],
      highlightPublicationsByYear: [],
      verticalProjects: [],
      horizontalProjects: [],
      patents: [],
      patentsByYear: [],
      softwareCopyrights: [],
      softwareCopyrightsByYear: [],
      awards: [],
      awardsByYear: [],
      videos: [],
      // 每页条数
      pageSize: 5,
      // 各分类当前页码
      publicationsPage: 1,
      highlightPublicationsPage: 1,
      verticalProjectsPage: 1,
      horizontalProjectsPage: 1,
      patentsPage: 1,
      softwareCopyrightsPage: 1,
      awardsPage: 1,
      videosPage: 1,
      // 各分类总数
      publicationsTotal: 0,
      highlightPublicationsTotal: 0,
      verticalProjectsTotal: 0,
      horizontalProjectsTotal: 0,
      patentsTotal: 0,
      softwareCopyrightsTotal: 0,
      awardsTotal: 0,
      videosTotal: 0,
      loadingPublications: false,
      loadingHighlightPublications: false,
      loadingVerticalProjects: false,
      loadingHorizontalProjects: false,
      loadingPatents: false,
      loadingSoftwareCopyrights: false,
      loadingAwards: false,
      loadingVideos: false,
      publicationsLoaded: false,
      highlightPublicationsLoaded: false,
      verticalProjectsLoaded: false,
      horizontalProjectsLoaded: false,
      patentsLoaded: false,
      softwareCopyrightsLoaded: false,
      awardsLoaded: false,
      videosLoaded: false,
    };
  },
  methods: {
    showPDF(file, title) {
      console.log("输出", file);
      // 如果未上传 PDF，则给出提示，不打开弹窗
      if (!file) {
        alert(this.$t("research.noPdf"));
        return;
      }
      this.pdfFile = file;
      this.pdfTitle = title;
      this.pdfVisible = true;
    },
    // 处理文件URL，确保正确拼接后端地址
    formatFileUrl(url) {
      if (!url || !url.trim() || url.trim() === "/") {
        return "";
      }
      // 如果已经是完整URL
      if (/^https?:\/\//.test(url)) {
        // 将http转换为https
        return url.replace(/^http:\/\//, "https://");
      }

      // 相对路径，拼接后端地址
      return `https://localhost:8090/${url.replace(/^\/+/, "")}`;
    },
    async getPublicationList() {
      this.loadingPublications = true;
      let res = await PublicationList({
        pageNum: this.publicationsPage,
        pageSize: this.pageSize,
      });
      if (res.code === 0) {
        this.publicationsTotal = res.total;
        let usersData = res.rows;
        // 将新加载的数据增量合并到年份分组中
        for (let i = 0; i < usersData.length; i++) {
          let paper = {
            title: usersData[i].title,
            authors: usersData[i].authorsInventorsRecipients,
            journal: usersData[i].organizationSource,
            doi: usersData[i].doi,
            abstract: usersData[i].description,
            file: this.formatFileUrl(usersData[i].pdfUrl),
            cover: usersData[i].imageUrl,
          };
          let year = usersData[i].achievementYear || "未知年份";
          this.publications.push(paper);
          // 查找已有的年份分组
          let group = this.publicationsByYear.find((g) => g.year === year);
          if (group) {
            group.items.push(paper);
          } else {
            this.publicationsByYear.push({ year: year, items: [paper] });
          }
        }
      }
      this.loadingPublications = false;
      this.publicationsLoaded = true;
    },
    loadMorePublications() {
      if (
        this.loadingPublications ||
        this.publications.length >= this.publicationsTotal
      ) {
        return;
      }
      this.publicationsPage++;
      this.getPublicationList();
    },
    async getHighlightPublicationList() {
      this.loadingHighlightPublications = true;
      let res = await AchievementList({
        pageNum: this.highlightPublicationsPage,
        pageSize: this.pageSize,
        achievementType: "highlight",
      });
      if (res.code === 0) {
        this.highlightPublicationsTotal = res.total;
        let usersData = res.rows;
        for (let i = 0; i < usersData.length; i++) {
          let paper = {
            title: usersData[i].title,
            authors: usersData[i].authorsInventorsRecipients,
            journal: usersData[i].organizationSource,
            doi: usersData[i].doi,
            abstract: usersData[i].description,
            file: this.formatFileUrl(usersData[i].pdfUrl),
            cover: usersData[i].imageUrl,
          };
          let year = usersData[i].achievementYear || "未知年份";
          this.highlightPublications.push(paper);
          let group = this.highlightPublicationsByYear.find(
            (g) => g.year === year
          );
          if (group) {
            group.items.push(paper);
          } else {
            this.highlightPublicationsByYear.push({ year: year, items: [paper] });
          }
        }
      }
      this.loadingHighlightPublications = false;
      this.highlightPublicationsLoaded = true;
    },
    loadMoreHighlightPublications() {
      if (
        this.loadingHighlightPublications ||
        this.highlightPublications.length >= this.highlightPublicationsTotal
      ) {
        return;
      }
      this.highlightPublicationsPage++;
      this.getHighlightPublicationList();
    },

    async getVerticalProjectList() {
      this.loadingVerticalProjects = true;
      let res = await ProjectList({
        pageNum: this.verticalProjectsPage,
        pageSize: this.pageSize,
        achievementType: "project_vertical",
      });
      if (res.code === 0) {
        this.verticalProjectsTotal = res.total;
        let projectsData = res.rows;
        for (let i = 0; i < projectsData.length; i++) {
          this.verticalProjects.push({
            title: projectsData[i].title,
            team: projectsData[i].authorsInventorsRecipients,
            source: projectsData[i].organizationSource,
            period: projectsData[i].achievementYear,
            desc: projectsData[i].description,
            file: this.formatFileUrl(projectsData[i].pdfUrl),
            cover: projectsData[i].imageUrl,
          });
        }
      }
      this.loadingVerticalProjects = false;
      this.verticalProjectsLoaded = true;
    },
    loadMoreVerticalProjects() {
      if (
        this.loadingVerticalProjects ||
        this.verticalProjects.length >= this.verticalProjectsTotal
      ) {
        return;
      }
      this.verticalProjectsPage++;
      this.getVerticalProjectList();
    },

    async getHorizontalProjectList() {
      this.loadingHorizontalProjects = true;
      let res = await ProjectList({
        pageNum: this.horizontalProjectsPage,
        pageSize: this.pageSize,
        achievementType: "project_horizontal",
      });
      if (res.code === 0) {
        this.horizontalProjectsTotal = res.total;
        let projectsData = res.rows;
        for (let i = 0; i < projectsData.length; i++) {
          this.horizontalProjects.push({
            title: projectsData[i].title,
            team: projectsData[i].authorsInventorsRecipients,
            source: projectsData[i].organizationSource,
            period: projectsData[i].achievementYear,
            desc: projectsData[i].description,
            file: this.formatFileUrl(projectsData[i].pdfUrl),
            cover: projectsData[i].imageUrl,
          });
        }
      }
      this.loadingHorizontalProjects = false;
      this.horizontalProjectsLoaded = true;
    },
    loadMoreHorizontalProjects() {
      if (
        this.loadingHorizontalProjects ||
        this.horizontalProjects.length >= this.horizontalProjectsTotal
      ) {
        return;
      }
      this.horizontalProjectsPage++;
      this.getHorizontalProjectList();
    },

    async getPatentList() {
      this.loadingPatents = true;
      let res = await PatentList({
        pageNum: this.patentsPage,
        pageSize: this.pageSize,
      });
      if (res.code === 0) {
        this.patentsTotal = res.total;
        let patentsData = res.rows;
        for (let i = 0; i < patentsData.length; i++) {
          let patent = {
            title: patentsData[i].title,
            number: patentsData[i].patentNumber,
            inventors: patentsData[i].authorsInventorsRecipients,
            date: patentsData[i].achievementYear,
            abstract: patentsData[i].description,
            certificate: patentsData[i].certificateUrl,
            file: this.formatFileUrl(patentsData[i].pdfUrl),
          };
          let year = patentsData[i].achievementYear || "未知年份";
          this.patents.push(patent);
          let group = this.patentsByYear.find((g) => g.year === year);
          if (group) {
            group.items.push(patent);
          } else {
            this.patentsByYear.push({ year: year, items: [patent] });
          }
        }
      }
      this.loadingPatents = false;
      this.patentsLoaded = true;
    },
    loadMorePatents() {
      if (this.loadingPatents || this.patents.length >= this.patentsTotal) {
        return;
      }
      this.patentsPage++;
      this.getPatentList();
    },
    async getSoftwareCopyrightList() {
      this.loadingSoftwareCopyrights = true;
      let res = await AchievementList({
        pageNum: this.softwareCopyrightsPage,
        pageSize: this.pageSize,
        achievementType: "software_copyright",
      });
      if (res.code === 0) {
        this.softwareCopyrightsTotal = res.total;
        let softwareCopyrightsData = res.rows;
        for (let i = 0; i < softwareCopyrightsData.length; i++) {
          let item = {
            title: softwareCopyrightsData[i].title,
            number: softwareCopyrightsData[i].patentNumber,
            inventors: softwareCopyrightsData[i].authorsInventorsRecipients,
            date: softwareCopyrightsData[i].achievementYear,
            abstract: softwareCopyrightsData[i].description,
            certificate: softwareCopyrightsData[i].certificateUrl,
            file: this.formatFileUrl(softwareCopyrightsData[i].pdfUrl),
          };
          let year = softwareCopyrightsData[i].achievementYear || "未知年份";
          this.softwareCopyrights.push(item);
          let group = this.softwareCopyrightsByYear.find((g) => g.year === year);
          if (group) {
            group.items.push(item);
          } else {
            this.softwareCopyrightsByYear.push({ year: year, items: [item] });
          }
        }
      }
      this.loadingSoftwareCopyrights = false;
      this.softwareCopyrightsLoaded = true;
    },
    loadMoreSoftwareCopyrights() {
      if (
        this.loadingSoftwareCopyrights ||
        this.softwareCopyrights.length >= this.softwareCopyrightsTotal
      ) {
        return;
      }
      this.softwareCopyrightsPage++;
      this.getSoftwareCopyrightList();
    },
    async getPwardList() {
      this.loadingAwards = true;
      let res = await PwardList({
        pageNum: this.awardsPage,
        pageSize: this.pageSize,
      });
      if (res.code === 0) {
        this.awardsTotal = res.total;
        let pwardsData = res.rows;
        for (let i = 0; i < pwardsData.length; i++) {
          let award = {
            title: pwardsData[i].title,
            year: pwardsData[i].achievementYear,
            recipients: pwardsData[i].authorsInventorsRecipients,
            org: pwardsData[i].organizationSource,
            desc: pwardsData[i].description,
            certificate: pwardsData[i].certificateUrl,
            file: this.formatFileUrl(pwardsData[i].pdfUrl),
          };
          let year = pwardsData[i].achievementYear || "未知年份";
          this.awards.push(award);
          let group = this.awardsByYear.find((g) => g.year === year);
          if (group) {
            group.items.push(award);
          } else {
            this.awardsByYear.push({ year: year, items: [award] });
          }
        }
      }
      this.loadingAwards = false;
      this.awardsLoaded = true;
    },
    loadMoreAwards() {
      if (this.loadingAwards || this.awards.length >= this.awardsTotal) {
        return;
      }
      this.awardsPage++;
      this.getPwardList();
    },
    async getVideoList() {
      this.loadingVideos = true;
      let res = await VideoList({
        pageNum: this.videosPage,
        pageSize: this.pageSize,
      });
      if (res.code === 0) {
        this.videosTotal = res.total;
        let videosData = res.rows;
        for (let i = 0; i < videosData.length; i++) {
          this.videos.push({
            title: videosData[i].title,
            year: videosData[i].achievementYear,
            desc: videosData[i].description,
            file: this.formatFileUrl(videosData[i].videoUrl),
            cover: videosData[i].imageUrl,
          });
        }
      }
      this.loadingVideos = false;
      this.videosLoaded = true;
    },
    loadMoreVideos() {
      if (this.loadingVideos || this.videos.length >= this.videosTotal) {
        return;
      }
      this.videosPage++;
      this.getVideoList();
    },
    handleScroll() {
      let scrollTop =
        window.pageYOffset ||
        document.documentElement.scrollTop ||
        document.body.scrollTop;
      let clientHeight = document.documentElement.clientHeight;
      let scrollHeight = document.documentElement.scrollHeight;
      if (scrollTop + clientHeight < scrollHeight - 120) {
        return;
      }
      if (this.activeTab === "publications") {
        if (this.publicationSubTab === "highlight") {
          this.loadMoreHighlightPublications();
          return;
        }
        this.loadMorePublications();
        return;
      }
      if (this.activeTab === "projects") {
        if (this.projectSubTab === "vertical") {
          this.loadMoreVerticalProjects();
          return;
        }
        this.loadMoreHorizontalProjects();
        return;
      }
      if (this.activeTab === "patents") {
        this.loadMorePatents();
        return;
      }
      if (this.activeTab === "softwareCopyrights") {
        this.loadMoreSoftwareCopyrights();
        return;
      }
      if (this.activeTab === "awards") {
        this.loadMoreAwards();
        return;
      }
      if (this.activeTab === "videos") {
        this.loadMoreVideos();
      }
    },
    showVideo(file, title) {
      console.log("输出视频点击", file);
      if (!file) {
        alert(this.$t("research.noVideo"));
        return;
      }
      this.videoFile = file;
      this.videoTitle = title;
      this.videoVisible = true;
    },
    closeVideo() {
      // 停止视频播放
      if (this.$refs.videoPlayer) {
        this.$refs.videoPlayer.pause();
        this.$refs.videoPlayer.currentTime = 0;
      }
      this.videoVisible = false;
      this.videoFile = "";
    },
  },
  mounted() {
    this.getPublicationList();
    this.getHighlightPublicationList();
    this.getVerticalProjectList();
    this.getHorizontalProjectList();
    this.getPatentList();
    this.getSoftwareCopyrightList();
    this.getPwardList();
    this.getVideoList();
    window.addEventListener("scroll", this.handleScroll);
  },
  beforeDestroy() {
    window.removeEventListener("scroll", this.handleScroll);
  },
  watch: {
    activeTab() {
      this.$nextTick(() => {
        this.handleScroll();
      });
    },
    projectSubTab() {
      this.$nextTick(() => {
        this.handleScroll();
      });
    },
    publicationSubTab() {
      this.$nextTick(() => {
        this.handleScroll();
      });
    },
  },
};
</script>

<style scoped>
.hero {
  background: linear-gradient(rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.5)),
    url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="1200" height="400"><rect width="1200" height="400" fill="%23004080"/><circle cx="200" cy="150" r="50" fill="%23FFD700" opacity="0.3"/><circle cx="800" cy="250" r="80" fill="%23FFD700" opacity="0.2"/><rect x="400" y="100" width="300" height="200" fill="%23FFD700" opacity="0.1"/></svg>');
  background-size: cover;
  height: 300px;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  color: #fff;
}

.hero-content h1 {
  font-size: 2.5em;
  margin-bottom: 20px;
}

.hero-content p {
  font-size: 1.1em;
}

.research-container {
  max-width: 1200px;
  margin: 60px auto;
  padding: 0 20px;
}

.research-intro {
  text-align: center;
}

.research-intro h2 {
  color: #003366;
  font-size: 1.8em;
  margin-bottom: 20px;
}

.research-tabs {
  display: flex;
  justify-content: center;
  margin-bottom: 20px;
}

.research-tab {
  padding: 10px 20px;
  cursor: pointer;
  border-bottom: 2px solid transparent;
}

.research-tab.active {
  color: #004080;
  border-bottom: 2px solid #004080;
  font-weight: bold;
}

.item-image {
  width: 120px;
  height: 160px;
  background-color: #e0e0e0;
  margin-right: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #666;
  font-size: 14px;
  overflow: hidden;
}

.item-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 4px;
}

.publication-item,
.project-item,
.patent-item,
.award-item,
.video-item {
  display: flex;
  margin-bottom: 20px;
  align-items: flex-start;
  transition: transform 0.3s ease, box-shadow 0.3s ease; /* 动画过渡 */
  background-color: #fff; /* 可加背景色，突出上浮效果 */
  border-radius: 8px;
  padding: 10px;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1); /* 默认阴影 */
}

.publication-item:hover,
.project-item:hover,
.patent-item:hover,
.award-item:hover,
.video-item:hover {
  transform: translateY(-8px); /* 上浮 8px */
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.2); /* 加深阴影 */
}

.project-item-text-only {
  padding: 16px 20px;
}

.project-item-text-only .item-content,
.patent-item-text-only .item-content {
  width: 100%;
}

.year-group {
  margin-bottom: 30px;
}

.year-header {
  font-size: 1.4em;
  font-weight: bold;
  color: #004080;
  border-left: 4px solid #004080;
  padding-left: 12px;
  margin-bottom: 16px;
}

.publication-title,
.project-title,
.patent-title,
.award-title {
  font-weight: bold;
  color: #003366;
  margin-bottom: 10px;
  font-size: 1.1em;
  cursor: pointer;
  transition: color 0.3s;
}

.publication-title:hover,
.project-title:hover,
.patent-title:hover,
.award-title:hover {
  color: #0066cc;
  text-decoration: underline;
}

.item-content {
  flex: 1;
}

.no-more {
  text-align: center;
  color: #999;
  font-size: 0.9rem;
  margin-top: 20px;
}

.loading-tip,
.empty-tip {
  text-align: center;
  color: #999;
  font-size: 0.9rem;
  margin-top: 20px;
}

.pdf-modal {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 999;
}

.pdf-modal-content {
  background: #fff;
  width: 80%;
  max-width: 1000px;
  border-radius: 8px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.pdf-modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 20px;
  background: #f5f5f5;
  border-bottom: 1px solid #ddd;
}

.pdf-modal-header h3 {
  margin: 0;
  font-size: 18px;
}

.close-btn {
  cursor: pointer;
  font-size: 24px;
}

.pdf-modal-body {
  padding: 0;
}

/* 科研项目二级子分类 tab */
.project-sub-tabs {
  display: flex;
  justify-content: center;
  margin-bottom: 20px;
  gap: 0;
}

.project-sub-tab {
  padding: 8px 24px;
  cursor: pointer;
  font-size: 0.95em;
  color: #666;
  border: 1px solid #ddd;
  background-color: #f9f9f9;
  transition: all 0.3s;
}

.project-sub-tab:first-child {
  border-radius: 20px 0 0 20px;
}

.project-sub-tab:last-child {
  border-radius: 0 20px 20px 0;
  border-left: none;
}

.project-sub-tab.active {
  background-color: #004080;
  color: #fff;
  border-color: #004080;
}

.project-sub-tab:hover:not(.active) {
  background-color: #e8f0fe;
  color: #004080;
}
</style>
